import type {FsCategoryLine} from '@/store/audit/fsCategoryStore';
import type {
    CommonWorkingPaper,
    CommonMaterialTestWorkingPaper,
    CommonTransactionWorkingPaper,
    BankWorkingPaper,
    FeesTransactionWorkingPaper,
    DebtTransactionWorkingPaper,
    DebtFeesTransactionWorkingPaper,
    CommonInvestmentWorkingPaper
} from '@/services/auditService/interface/common';
import type {
    CommonRow,
    BankRow,
    TestRow,
    CommonFeesRow,
    CommonDebtRow,
    DueFromToDirRow
} from '@/services/auditService/interface/wp-row';
import type {
    DueFromToDirectorsWorkingPaper,
    GainLossExchangeWorkingPaper,
    GainLossDisposalWorkingPaper
} from '@/services/auditService/interface/dynamic';
import type {
    CommissionPaymentWorkingPaper,
    WithholdingTaxWorkingPaper,
    DonationWorkingPaper,
    PurchasesWorkingPaper,
    RentalExpensesWorkingPaper,
    SalariesAllowanceWorkingPaper,
    SalariesWageWorkingPaper,
    DirectorSalaryWorkingPaper,
    MpfSummaryWorkingPaper,
    DepreciationSummaryWorkingPaper,
} from '@/services/auditService/interface/expenses';
import type {
    SalesIncomeWorkingPaper,
    InterestIncomeWorkingPaper
} from '@/services/auditService/interface/income';
import type {BankLoanWorkingPaper, VehicleLoanWorkingPaper, InterestSummaryWorkingPaper} from '@/services/auditService/interface/loan';

import {resolvePayloadStrategy} from '@/adjustment/adapters';
import {resolveFlatEquityAdjustment} from '@/pages/components/audit/adjustment/adjust/utils/classifyRows';
import {
    saveOrUpdate,
    saveAndPosted,
    type AuditAdjustmentLine,
    type AuditAdjustmentHeader
} from '@/services/auditService/auditAdjustmentService';
import {useAdjustmentStore, useAdjustDraftStore} from '@/store/audit';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import {
    InventoriesWorkingPaper,
    PropertyPlantEquipmentWorkingPaper,
    IntangibleAssetsWorkingPaper,
    InvestmentInTaxiCabsAndLicensesWorkingPaper
} from "@/services/auditService/interface/asset";

/**
 * 計算是否不平衡
 * @param finalAmount - 資產負債表最終金額
 * @param totalFinal - 計算的總金額
 * @param tolerance - 容差值（默認 0.01）
 * @returns 是否不平衡
 */
export const calculateIsUnbalanced = (
    finalAmount: number,
    totalFinal: number,
    tolerance: number = 0.01
): boolean => {
    const diff = Math.abs(finalAmount - totalFinal);
    return diff > tolerance;
};

/**
 * 計算金額差異（adjustment 總和）
 * 計算公式：total adjustment
 * 只計算當前 adjustment 值，不包含歷史 adjustments 數組
 * @param rows - CommonRow 數組
 * @returns adjustment 總和
 */
export const calculateAmount = (rows: CommonRow[]): number => {
    const totalAdjustment = rows.reduce((sum, row) => {
        // 只使用當前 adjustment 值，不使用歷史 adjustments 數組
        return sum + (Number((row as any).adjustment) || 0);
    }, 0);

    return totalAdjustment;
};

export interface ValidationResult {
    code: string;
    strategy: any;
}

export interface ShowSnackbarFn {
    (message: string, severity: 'warning' | 'error' | 'success'): void;
}

/**
 * 驗證並獲取 scheduleCode 和 strategy
 * @param categoryLine - 分類行數據
 * @param showSnackbar - 顯示消息的回調函數
 * @returns 驗證結果，失敗返回 null
 */
export const validateAndGetStrategy = (
    categoryLine: FsCategoryLine | null,
    showSnackbar: ShowSnackbarFn
): ValidationResult | null => {
    if (!categoryLine) {
        showSnackbar('No categoryLine available', 'warning');
        return null;
    }

    // Use code field first, fallback to scheduleCode for backward compatibility
    const code = String(categoryLine?.code || categoryLine?.scheduleCode || '').trim();
    if (!code) {
        showSnackbar('No code available', 'warning');
        return null;
    }

    // Prefer scheduleCode (legacy dialog key), then FS code, then custom-node templateCodeRef.
    // Custom codes (e.g. PL-COT-ABC1) are not in strategyMap; DialogLauncher uses templateCodeRef.
    const scheduleCode = String(categoryLine?.scheduleCode || '').trim();
    const templateCodeRef = String(
        (categoryLine as {templateCodeRef?: string | null})?.templateCodeRef || '',
    ).trim();
    const isCustomNode = (categoryLine as {isCustom?: boolean; customNodeId?: number | null})?.isCustom === true
        || (categoryLine as {customNodeId?: number | null})?.customNodeId != null;
    const strategy = resolvePayloadStrategy([
        scheduleCode,
        code,
        templateCodeRef,
        isCustomNode ? 'CommonTransactionWp' : '',
    ]);
    if (!strategy) {
        showSnackbar(`No strategy found for code: ${code}`, 'warning');
        return null;
    }

    // Return code for new system
    return {code, strategy};
};

/**
 * 創建 Snackbar 消息顯示函數
 * @param setMessage - 設置消息的回調
 * @param setSeverity - 設置嚴重程度的回調
 * @param setOpen - 設置打開狀態的回調
 * @returns 顯示 Snackbar 的函數
 */
export const createShowSnackbar = (
    setMessage: (msg: string) => void,
    setSeverity: (severity: 'warning' | 'error' | 'success') => void,
    setOpen: (open: boolean) => void
): ShowSnackbarFn => {
    return (message: string, severity: 'warning' | 'error' | 'success') => {
        setMessage(message);
        setSeverity(severity);
        setOpen(true);
    };
};

/**
 * 从 adjustmentLine.details 中提取行数据数组
 * 支持多种数据结构格式和策略类型，自动识别并提取正确的数据
 *
 * @param details - adjustmentLine.details 数据
 * @param propertyName - 可选，指定要提取的属性名称（如 'commonRow', 'bankRow', 'feesRow' 等）
 * @param strategyType - 可选，指定策略类型以返回完整的工作底稿对象
 * @param scheduleCode
 * @returns 根据策略类型返回对应的数据结构
 *
 * 支持的策略类型及其数据结构：
 * - commonStrategy: CommonWorkingPaper { commonRow?: CommonRow[] }
 * - commonMaterialTestStrategy: CommonMaterialTestWorkingPaper { commonRow?, relatedRows?, testRow? }
 * - commonTransactionStrategy: CommonTransactionWorkingPaper { commonRow?, relatedRows? }
 * - bankStrategy: BankWorkingPaper { bankRow?, commonRow? }
 * - feesTransactionStrategy: FeesTransactionWorkingPaper { feesRow?, relatedRows? }
 * - debtTransactionStrategy: DebtTransactionWorkingPaper { debtRow?, relatedRows? }
 * - inventories: InventoriesWorkingPaper { commonRow?, testRow? }
 * - commonInvestment: commonInvestmentWorkingPaper { intangibleRows?, principalSubsidiaryRow?, adjustments?: { nbvCfAdjusts } }
 * - dueFromToDirectors: DueFromToDirectorsWorkingPaper { dueFromToDirectors? }
 * - gainLossExchange: GainLossExchangeWorkingPaper { gainExchange?, lossExchange? }
 * - gainLossDisposal: GainLossDisposalWorkingPaper { gainDisposal?, lossDisposal?, relatedRows? }
 * - commissionPayment: CommissionPaymentWorkingPaper { commissionPayments?, relatedItems? }
 * - donation: DonationWorkingPaper { taxDeductible?, nonTaxDeductible?, relatedItems? }
 * - purchases: PurchasesWorkingPaper { purchases?, relatedItems?, transactionTest, cutOffThisYearTest, cutOffNextYearTest }
 * - salesIncome: SalesIncomeWorkingPaper { salesIncome?, relatedRows?, transactionTest, cutOffThisYearTest, cutOffNextYearTest }
 * - propertyPlantEquipment: PropertyPlantEquipmentWorkingPaper { testRow?, locationOfPropertyRow?, commercialAllowanceRow?, etc. }
 * - propertyPlantEquipment: PropertyPlantEquipmentWorkingPaper { testRow?, locationOfPropertyRow?, commercialAllowanceRow?, etc. }
 * - intangibleAssets: IntangibleAssetsWorkingPaper { intangibleRows?, adjustments?: { amorChgAdjusts, nbvCfAdjusts } }
 * - investmentInTaxiCabsAndLicenses: InvestmentInTaxiCabsAndLicensesWorkingPaper { taxiLicenseRow?, fullPayTaxiCabRow?, hirePurchaseTaxiCabRow?, adjustments?: { amorChgAdjusts, nbvCfAdjusts } }
 * - interestIncome: InterestIncomeWorkingPaper { bankInterestIncome?, otherInterestIncome?, relatedRows? }
 * - salariesAllowance: ISalariesAllowance { staffRow?, workerRow?, directorRow?, directorQuarterRow, staffTotalAdj, workerTotalAdj, directorTotalAdj, mpfTotalAdj, quarterTotalAdj }
 * - salariesWage: SalariesWageWorkingPaper { salary? }
 * - directorSalary: DirectorSalaryWorkingPaper { directorSalary?, quarter? }
 * - mpfSummary: MpfSummaryWorkingPaper { mpfSummary? }
 * - depreciationSummary: DepreciationSummaryWorkingPaper { depreciationSummary? }
 * - interestSummary: InterestSummaryWorkingPaper { interestSummary?, addBackEnabled?, addBackSourceCodes?, addBackAmount?, addBackPercent? }
 *
 * 支持的数据结构：
 * 1. [{ commonRow: [...] }] - 嵌套在指定属性中
 * 2. [{ rows: [...] }] - 嵌套在 rows 属性中
 * 3. [{ details: [...] }] - 嵌套在 details 属性中
 * 4. [{ data: [...] }] - 直接的数组
 * 5. { commonRow: [...], bankRow: [...] } - 对象形式（多属性）
 */
export const extractRowsDetails = (
    details: any,
    propertyName?: string,
    strategyType?: 'common' | 'commonMaterialTest' | 'commonTransaction' | 'bank' | 'feesTransaction' | 'debtTransaction' | 'debtFeesTransaction' | 'inventories' | 'commonInvestment' | 'dueFromToDirectors' | 'gainLossExchange' | 'gainLossDisposal' | 'commissionPayment' | 'withholdingTax' | 'donation' | 'purchases' | 'salesIncome' | 'propertyPlantEquipment' | 'intangibleAssets' | 'investmentInTaxiCabsAndLicenses' | 'interestIncome' | 'salariesAllowance' | 'salariesWage' | 'directorSalary' | 'mpfSummary' | 'depreciationSummary' | 'interestSummary' | 'loan' | 'rentalExpenses',
    code?: string
): any => {
    // 如果 details 为空或 undefined，根据策略类型返回默认值
    if (!details) {
        return strategyType ? getDefaultPayload(strategyType, code) : [];
    }

    // 如果指定了策略类型，按策略类型提取完整数据结构
    if (strategyType) {
        return extractByStrategy(details, strategyType, code);
    }

    // 常见的属性名称列表（按优先级排序）- 包含所有策略的行类型
    const commonPropertyNames = [
        propertyName, // 用户指定的属性名优先
        'commonRow',
        'bankRow',
        'feesRow',
        'debtRow',
        'debtFeeRow',
        'testRow',
        'relatedRows',
        'inventoryRow',
        'principalSubsidiaryRow',
        'rows',
        'details',
        'data',
        'items'
    ].filter(Boolean) as string[];

    // 情况 1: 如果 details 是数组
    if (Array.isArray(details)) {
        // 情况 1.1: 空数组
        if (details.length === 0) {
            return [];
        }

        let firstItem = details[0];

        // 如果第一个元素包含 payload 字段，使用 payload 中的数据
        if (firstItem && typeof firstItem === 'object' && firstItem.payload) {
            firstItem = firstItem.payload;
        }

        // 情况 1.2: 尝试从第一个元素的属性中提取
        if (firstItem && typeof firstItem === 'object') {
            // 按优先级尝试提取
            for (const propName of commonPropertyNames) {
                if (firstItem[propName] && Array.isArray(firstItem[propName])) {
                    return firstItem[propName];
                }
            }

            // 如果没有找到已知属性，尝试找第一个数组类型的属性
            const arrayProp = Object.keys(firstItem).find(key => Array.isArray(firstItem[key]));
            if (arrayProp) {
                return firstItem[arrayProp];
            }
        }

        // 情况 1.3: 直接是 CommonRow 数组（检查是否有 id 或 item 属性）
        if (firstItem?.id !== undefined || firstItem?.item !== undefined) {
            return details;
        }

        return details;
    }

    // 情况 2: 如果 details 是对象
    if (typeof details === 'object') {
        // Since details is now Record<string, any>, we use details directly
        // But still check for legacy payload structure for backward compatibility
        let dataObj = details;
        if (details.payload && typeof details.payload === 'object') {
            dataObj = details.payload;
        }

        // 按优先级尝试提取
        for (const propName of commonPropertyNames) {
            if (dataObj[propName] && Array.isArray(dataObj[propName])) {
                return dataObj[propName];
            }
        }

        // 特殊情况：递归处理嵌套的 details
        if (dataObj.details && typeof dataObj.details === 'object') {
            return extractRowsDetails(dataObj.details, propertyName, strategyType, code);
        }

        // 如果没有找到已知属性，尝试找第一个数组类型的属性
        const arrayProp = Object.keys(dataObj).find(key => Array.isArray(dataObj[key]));
        if (arrayProp) {
            return dataObj[arrayProp];
        }

        // 对象本身就是一个 CommonRow
        if (dataObj.id !== undefined || dataObj.item !== undefined) {
            return [dataObj];
        }

        return [];
    }

    // 其他情况：根据策略类型返回默认值
    return strategyType ? getDefaultPayload(strategyType, code) : [];
};

/**
 * 根据策略类型从 details 中提取完整的工作底稿数据结构
 * @param details - 原始数据
 * @param strategyType - 策略类型
 * @returns 对应策略的工作底稿对象
 */
const extractByStrategy = (
    details: any,
    strategyType: 'common' | 'commonMaterialTest' | 'commonTransaction' | 'bank' | 'feesTransaction' | 'debtTransaction' | 'debtFeesTransaction' | 'inventories' | 'commonInvestment' | 'dueFromToDirectors' | 'gainLossExchange' | 'gainLossDisposal' | 'commissionPayment' | 'withholdingTax' | 'donation' | 'purchases' | 'salesIncome' | 'propertyPlantEquipment' | 'intangibleAssets' | 'investmentInTaxiCabsAndLicenses' | 'interestIncome' | 'salariesAllowance' | 'salariesWage' | 'directorSalary' | 'mpfSummary' | 'depreciationSummary' | 'interestSummary' | 'loan' | 'rentalExpenses',
    code?: string
): any => {

    // 统一处理：如果是数组，取第一个元素
    let data = Array.isArray(details) ? (details[0] || {}) : details;

    // 如果数据包含 payload 字段（新格式），则使用 payload 中的数据
    if (data && typeof data === 'object' && data.payload) {
        data = data.payload;
    }

    // 处理 header 结构：如果数据包含 lines 数组，提取 lines[0].details[0]
    // 这种情况发生在 useAdjustDialogData 从后端加载整个 header 对象时
    if (data && typeof data === 'object' && data.lines && Array.isArray(data.lines)) {
        const firstLine = data.lines[0];
        if (firstLine && firstLine.details && Array.isArray(firstLine.details)) {
            data = firstLine.details[0] || {};
        }
    }

    switch (strategyType) {
        case 'common':
            return {
                commonRow: extractArrayProperty(data, 'commonRow')
            } as CommonWorkingPaper;

        case 'commonMaterialTest':
            return {
                commonRow: extractArrayProperty(data, 'commonRow'),
                relatedRows: extractArrayProperty(data, 'relatedRows'),
                testRow: extractArrayProperty(data, 'testRow'),
                addBackEnabled: data?.addBackEnabled === true,
            } as CommonMaterialTestWorkingPaper;

        case 'commonTransaction':
            return {
                commonRow: extractArrayProperty(data, 'commonRow'),
                relatedRows: extractArrayProperty(data, 'relatedRows')
            } as CommonTransactionWorkingPaper;

        case 'bank':
            return {
                bankRow: extractArrayProperty(data, 'bankRow'),
                commonRow: extractArrayProperty(data, 'commonRow')
            } as BankWorkingPaper;

        case 'feesTransaction':
            return {
                feesRow: extractArrayProperty(data, 'feesRow'),
                relatedRows: extractArrayProperty(data, 'relatedRows')
            } as FeesTransactionWorkingPaper;

        case 'debtTransaction':
            return {
                debtRow: extractArrayProperty(data, 'debtRow'),
                relatedRows: extractArrayProperty(data, 'relatedRows')
            } as DebtTransactionWorkingPaper;

        case 'debtFeesTransaction':
            return {
                debtFeeRow: extractArrayProperty(data, 'debtFeeRow'),
                relatedRows: extractArrayProperty(data, 'relatedRows'),
                addBackEnabled: data?.addBackEnabled === true,
            } as DebtFeesTransactionWorkingPaper;

        case 'inventories':
            return {
                commonRow: extractArrayProperty(data, 'commonRow'),
                testRow: extractArrayProperty(data, 'testRow')
            } as InventoriesWorkingPaper;

        case 'commonInvestment':
            return {
                intangibleRows: extractArrayProperty(data, 'intangibleRows'),
                // legacy keys kept for any stale draft/cache until cleared
                costRow: extractArrayProperty(data, 'costRow'),
                impairmentLossRow: extractArrayProperty(data, 'impairmentLossRow'),
                principalSubsidiaryRow: extractArrayProperty(data, 'principalSubsidiaryRow'),
                // GET schedule lists for Grand Total NBV C/F (IA parity)
                adjustments: extractIaWpLevelAdjustments(data),
            } as CommonInvestmentWorkingPaper;

        case 'dueFromToDirectors':
            const directRows = extractArrayProperty(data, 'dueFromToDirRow');
            const legacyRows = extractArrayProperty(data, 'dueFromToDirectors').map((r: any) => ({
                id: r.id,
                annualId: r.annualId,
                code: r.code,
                directorId: r.directorId,
                directorName: r.directorName ?? r.item,
                perClient: r.perClient,
                adjustment: r.adjustment,
                adjustHeaderId: r.adjustHeaderId ?? undefined,
                lastYearFinal: r.lastYearFinal,
                finalChange: r.finalChange ?? null,
            } as DueFromToDirRow));

            return {
                dueFromToDirRow: (directRows && directRows.length > 0) ? directRows : legacyRows,
            } as DueFromToDirectorsWorkingPaper;

        case 'gainLossExchange':
            return {
                gainExchange: extractArrayProperty(data, 'gainExchange'),
                lossExchange: extractArrayProperty(data, 'lossExchange')
            } as GainLossExchangeWorkingPaper;

        case 'gainLossDisposal':
            return {
                // PPE disposal rows
                locationOfPropertyDisRow: extractArrayProperty(data, 'locationOfPropertyDisRow'),
                commercialAllowanceDisRow: extractArrayProperty(data, 'commercialAllowanceDisRow'),
                industrialAllowanceDisRow: extractArrayProperty(data, 'industrialAllowanceDisRow'),
                furnitureFixtureDisRow: extractArrayProperty(data, 'furnitureFixtureDisRow'),
                computerHardSoftDisRow: extractArrayProperty(data, 'computerHardSoftDisRow'),
                fullPayMotorDisRow: extractArrayProperty(data, 'fullPayMotorDisRow'),
                hirePurchaseMotorDisRow: extractArrayProperty(data, 'hirePurchaseMotorDisRow'),
                machineryDisRow: extractArrayProperty(data, 'machineryDisRow'),
                liftEscalatorDisRow: extractArrayProperty(data, 'liftEscalatorDisRow'),
                equipmentDisRow: extractArrayProperty(data, 'equipmentDisRow'),
                ppeTitle: data?.ppeTitle && typeof data.ppeTitle === 'object' && !Array.isArray(data.ppeTitle)
                    ? data.ppeTitle as Record<string, string>
                    : undefined,
                irdRateDefaults:
                    data?.irdRateDefaults && typeof data.irdRateDefaults === 'object' && !Array.isArray(data.irdRateDefaults)
                        ? data.irdRateDefaults as Record<string, Record<string, number>>
                        : undefined,
                // Other disposal rows
                subsidiaryRow: extractArrayProperty(data, 'subsidiaryRow'),
                associateRow: extractArrayProperty(data, 'associateRow'),
                intangibleRow: extractArrayProperty(data, 'intangibleRow'),
                taxiLicenseDisRow: extractArrayProperty(data, 'taxiLicenseDisRow'),
                fullPayTaxiCabDisRow: extractArrayProperty(data, 'fullPayTaxiCabDisRow'),
                hirePurchaseTaxiCabDisRow: extractArrayProperty(data, 'hirePurchaseTaxiCabDisRow'),
                trustsRow: extractArrayProperty(data, 'trustsRow'),
            } as GainLossDisposalWorkingPaper;

        case 'commissionPayment':
            return {
                commissionPayments: extractArrayProperty(data, 'commissionPayments', ['commissionPaymentRows']),
                relatedItems: extractArrayProperty(data, 'relatedItems', ['relatedRows'])
            } as CommissionPaymentWorkingPaper;

        case 'withholdingTax':
            return {
                whtRows: extractArrayProperty(data, 'whtRows'),
                relatedItems: extractArrayProperty(data, 'relatedItems', ['relatedRows'])
            } as WithholdingTaxWorkingPaper;

        case 'donation':
            return {
                taxDeductible: extractArrayProperty(data, 'taxDeductible', ['taxDeductibleRows']),
                nonTaxDeductible: extractArrayProperty(data, 'nonTaxDeductible', ['nonTaxDeductibleRows']),
                relatedItems: extractArrayProperty(data, 'relatedItems', ['relatedRows'])
            } as DonationWorkingPaper;

        case 'purchases':
            return {
                purchases: extractArrayProperty(data, 'purchases', ['purchasesRows']),
                relatedItems: extractArrayProperty(data, 'relatedItems', ['relatedRows']),
                transactionTest: extractArrayProperty(data, 'transactionTest', ['transactionRows']),
                cutOffThisYearTest: extractArrayProperty(data, 'cutOffThisYearTest', ['cutOffThisRows']),
                cutOffNextYearTest: extractArrayProperty(data, 'cutOffNextYearTest', ['cutOffNextRows'])
            } as PurchasesWorkingPaper;

        case 'salesIncome':
            return {
                salesIncome: extractArrayProperty(data, 'salesIncome'),
                relatedRows: extractArrayProperty(data, 'relatedRows'),
                transactionTest: extractArrayProperty(data, 'transactionTest'),
                cutOffThisYearTest: extractArrayProperty(data, 'cutOffThisYearTest'),
                cutOffNextYearTest: extractArrayProperty(data, 'cutOffNextYearTest'),
                annualInfo: data.annualInfo
            } as SalesIncomeWorkingPaper;

        case 'propertyPlantEquipment':
            // PL-AE-DEP + Taxi nest only — BS-NCA-PPE / PPE DEP xxxRow unchanged
            if (
                String(code || '').trim().toUpperCase() === 'PL-AE-DEP'
                && Array.isArray(data?.intangibleRows)
                && data.intangibleRows.length > 0
            ) {
                return {
                    intangibleRows: extractArrayProperty(data, 'intangibleRows'),
                } as InvestmentInTaxiCabsAndLicensesWorkingPaper;
            }
            return {
                testRow: extractArrayProperty(data, 'testRow'),
                locationOfPropertyRow: extractArrayProperty(data, 'locationOfPropertyRow'),
                commercialAllowanceRow: extractArrayProperty(data, 'commercialAllowanceRow'),
                industrialAllowanceRow: extractArrayProperty(data, 'industrialAllowanceRow'),
                furnitureFixtureRow: extractArrayProperty(data, 'furnitureFixtureRow'),
                computerHardSoftRow: extractArrayProperty(data, 'computerHardSoftRow'),
                fullPayMotorRow: extractArrayProperty(data, 'fullPayMotorRow'),
                hirePurchaseMotorRow: extractArrayProperty(data, 'hirePurchaseMotorRow'),
                machineryRow: extractArrayProperty(data, 'machineryRow'),
                liftEscalatorRow: extractArrayProperty(data, 'liftEscalatorRow'),
                equipmentRow: extractArrayProperty(data, 'equipmentRow'),
                // Existing ppeRate may have null equipmentIrdRate (legacy DB); default 20 for tax pool.
                ppeRate: data.ppeRate && typeof data.ppeRate === 'object'
                    ? {
                        ...data.ppeRate,
                        // Annual value wins; else admin irdRateDefaults.equipmentRow; else 20.
                        equipmentIrdRate: data.ppeRate.equipmentIrdRate
                            ?? data.irdRateDefaults?.equipmentRow
                            ?? 20,
                    }
                    : {
                        locationOfPropertyRate: data.locationOfPropertyRate ?? 4,
                        commercialAllowanceRate: data.commercialAllowanceRate ?? 20,
                        industrialAllowanceRate: data.industrialAllowanceRate ?? 20,
                        furnitureFixtureRate: data.furnitureFixtureRate ?? 20,
                        computerHardSoftRate: data.computerHardSoftRate ?? 20,
                        fullPayMotorRate: data.fullPayMotorRate ?? 30,
                        hirePurchaseMotorRate: data.hirePurchaseMotorRate ?? 30,
                        machineryRate: data.machineryRate ?? 20,
                        liftEscalatorRate: data.liftEscalatorRate ?? 20,
                        equipmentRate: data.equipmentRate ?? 20,
                        equipmentIrdRate: data.equipmentIrdRate ?? 20,
                    },
                irdRateDefaults:
                    data.irdRateDefaults && typeof data.irdRateDefaults === 'object' && !Array.isArray(data.irdRateDefaults)
                        ? data.irdRateDefaults
                        : undefined,
                ppeTitle: data.ppeTitle && typeof data.ppeTitle === 'object' ? data.ppeTitle : undefined,
                adjustments: extractPpeWpLevelAdjustments(data),
            } as PropertyPlantEquipmentWorkingPaper;

        case 'intangibleAssets':
            return {
                intangibleRows: extractArrayProperty(data, 'intangibleRows'),
                adjustments: extractIaWpLevelAdjustments(data),
            } as IntangibleAssetsWorkingPaper;

        case 'investmentInTaxiCabsAndLicenses':
            return {
                taxiLicenseRow: extractArrayProperty(data, 'taxiLicenseRow'),
                fullPayTaxiCabRow: extractArrayProperty(data, 'fullPayTaxiCabRow'),
                hirePurchaseTaxiCabRow: extractArrayProperty(data, 'hirePurchaseTaxiCabRow'),
                itlTitle: data.itlTitle && typeof data.itlTitle === 'object' ? data.itlTitle : undefined,
                // Legacy flat rows (pre-section split)
                intangibleRows: extractArrayProperty(data, 'intangibleRows'),
                adjustments: extractIaWpLevelAdjustments(data),
            } as InvestmentInTaxiCabsAndLicensesWorkingPaper;

        case 'interestIncome':
            return {
                bankInterestIncome: extractArrayProperty(data, 'bankInterestIncome'),
                otherInterestIncome: extractArrayProperty(data, 'otherInterestIncome'),
                relatedRows: extractArrayProperty(data, 'relatedRows')
            } as InterestIncomeWorkingPaper;

        case 'salariesAllowance':
            return {
                staffRow: extractArrayProperty(data, 'staffRow'),
                workerRow: extractArrayProperty(data, 'workerRow'),
                directorRow: extractArrayProperty(data, 'directorRow'),
                directorQuarterRow: extractArrayProperty(data, 'directorQuarterRow'),
            } as SalariesAllowanceWorkingPaper;

        case 'salariesWage':
            return {
                salary: extractArrayProperty(data, 'salary'),
            } as SalariesWageWorkingPaper;

        case 'directorSalary':
            return {
                directorSalary: extractArrayProperty(data, 'directorSalary'),
                quarter: extractArrayProperty(data, 'quarter'),
            } as DirectorSalaryWorkingPaper;

        case 'mpfSummary':
            return {
                mpfSummary: data.mpfSummary || { salary: {}, wages: {}, director: {}, total: {} },
            } as MpfSummaryWorkingPaper;

        case 'depreciationSummary':
            return {
                depreciationSummary: data.depreciationSummary || { rows: [], total: {} },
            } as DepreciationSummaryWorkingPaper;

        case 'interestSummary':
            return {
                interestSummary: data.interestSummary || { bank: {}, vehicle: {}, total: {} },
                addBackEnabled: data?.addBackEnabled === true,
                addBackSourceCodes: Array.isArray(data?.addBackSourceCodes)
                    ? data.addBackSourceCodes.map((c: unknown) => String(c).toUpperCase())
                    : [],
                addBackAmount: data?.addBackAmount ?? null,
                addBackPercent: data?.addBackPercent ?? null,
            } as InterestSummaryWorkingPaper;

        case 'loan':
            // For bank loans, return BankLoanWorkingPaper
            // Support both old codes (BANK_LOAN, NON_BANK_LOAN) and new codes (BS-CL-BLS, BS-NCL-BLS)
            if (code === 'BANK_LOAN' || code === 'NON_BANK_LOAN' ||
                code === 'BS-CL-BLS' || code === 'BS-NCL-BLS') {
                return {
                    bankLoans: extractArrayProperty(data, 'bankLoans'),
                } as BankLoanWorkingPaper;
            }
            // For vehicle loans (BS-CL-OFL, BS-NCL-OFL), return VehicleLoanWorkingPaper
            return {
                vehicleLoans: extractArrayProperty(data, 'vehicleLoans'),
            } as VehicleLoanWorkingPaper;

        case 'rentalExpenses':
            return {
                rows: extractArrayProperty(data, 'rows'),
                rentalOtherInfo: data.rentalOtherInfo || {
                    monthsForPeriod: data.monthsForPeriod ?? null,
                    extractPL: data.extractPL ?? '0',
                    deletedMonthsByCol: data.deletedMonthsByCol || {},
                    rentalTotalsByCol: data.rentalTotalsByCol || {},
                },
                relatedItems: extractArrayProperty(data, 'relatedItems', ['relatedRows'])
            } as RentalExpensesWorkingPaper;

        default:
            return getDefaultPayload(strategyType, code);
    }
};

/**
 * 从对象中提取指定的数组属性
 * @param obj - 源对象
 * @param propName - 属性名
 * @returns 数组或空数组
 */
const extractArrayProperty = (obj: any, propName: string, alternatePropNames?: string[]): any[] => {
    if (!obj || typeof obj !== 'object') return [];

    // 先尝试主属性名
    let value = obj[propName];
    if (Array.isArray(value)) return value;

    // 如果主属性名没有找到，尝试备选属性名
    if (alternatePropNames && alternatePropNames.length > 0) {
        for (const altName of alternatePropNames) {
            value = obj[altName];
            if (Array.isArray(value)) return value;
        }
    }

    return [];
};

/**
 * IA WP-level `adjustments` only.
 * - GET schedule lists: `{amorChgAdjusts, nbvCfAdjusts}` (plus legacy top-level `*Adjustments`)
 * - Leave row-level field VO (`amorChgAdjs` / `nbvCfAdjs`) untouched on intangibleRows.
 */
const extractIaWpLevelAdjustments = (data: any): any => {
    if (!data || typeof data !== 'object') return undefined;
    const raw = data.adjustments;
    if (Array.isArray(raw)) return raw;
    const nested = raw && typeof raw === 'object' ? raw : null;
    const looksLikeFieldVo = !!(nested && ('amorChgAdjs' in nested || 'nbvCfAdjs' in nested));
    if (looksLikeFieldVo) return nested;

    const nestedAmor = extractArrayProperty(nested, 'amorChgAdjusts');
    const legacyAmor = extractArrayProperty(data, 'amorChgAdjustments');
    const amorChgAdjusts = nestedAmor.length > 0 ? nestedAmor : legacyAmor;
    const nestedNbv = extractArrayProperty(nested, 'nbvCfAdjusts');
    const legacyNbv = extractArrayProperty(data, 'nbvCfAdjustments');
    const nbvCfAdjusts = nestedNbv.length > 0 ? nestedNbv : legacyNbv;
    const result: {amorChgAdjusts?: any[]; nbvCfAdjusts?: any[]} = {};
    if (amorChgAdjusts.length > 0) result.amorChgAdjusts = amorChgAdjusts;
    if (nbvCfAdjusts.length > 0) result.nbvCfAdjusts = nbvCfAdjusts;
    if (Object.keys(result).length > 0) return result;
    return nested ?? undefined;
};

/**
 * PPE WP-level `adjustments` only.
 * - GET schedule lists: `{depnChgAdjusts, nbvCfAdjusts}` (plus legacy top-level `depnChgAdjustments`)
 * - Leave row-level field VO (`costAdjs` / `addAdjs` / …) and classic arrays untouched
 *   so extractRowsDetails does not rewrite Cost/Add/NBV field-adj payloads.
 */
const extractPpeWpLevelAdjustments = (data: any): any => {
    if (!data || typeof data !== 'object') return undefined;
    const raw = data.adjustments;
    if (Array.isArray(raw)) return raw;
    const nested = raw && typeof raw === 'object' ? raw : null;
    const looksLikeFieldVo = !!(nested && (
        'costAdjs' in nested
        || 'addAdjs' in nested
        || 'nbvCfAdjs' in nested
        || 'depnChgAdjs' in nested
        || 'accDepAdjs' in nested
    ));
    if (looksLikeFieldVo) return nested;

    const nestedDepn = extractArrayProperty(nested, 'depnChgAdjusts');
    const legacyDepn = extractArrayProperty(data, 'depnChgAdjustments');
    const depnChgAdjusts = nestedDepn.length > 0 ? nestedDepn : legacyDepn;
    const nbvCfAdjusts = extractArrayProperty(nested, 'nbvCfAdjusts');
    const result: {depnChgAdjusts?: any[]; nbvCfAdjusts?: any[]} = {};
    if (depnChgAdjusts.length > 0) result.depnChgAdjusts = depnChgAdjusts;
    if (nbvCfAdjusts.length > 0) result.nbvCfAdjusts = nbvCfAdjusts;
    if (Object.keys(result).length > 0) return result;
    return nested ?? undefined;
};

/**
 * 根据策略类型返回默认的工作底稿对象
 * @param strategyType - 策略类型
 * @returns 默认的工作底稿对象
 */
const getDefaultPayload = (
    strategyType: 'common' | 'commonMaterialTest' | 'commonTransaction' | 'bank' | 'feesTransaction' | 'debtTransaction' | 'debtFeesTransaction' | 'inventories' | 'commonInvestment' | 'dueFromToDirectors' | 'gainLossExchange' | 'gainLossDisposal' | 'commissionPayment' | 'withholdingTax' | 'donation' | 'purchases' | 'salesIncome' | 'propertyPlantEquipment' | 'intangibleAssets' | 'investmentInTaxiCabsAndLicenses' | 'interestIncome' | 'salariesAllowance' | 'salariesWage' | 'directorSalary' | 'mpfSummary' | 'depreciationSummary' | 'interestSummary' | 'loan' | 'rentalExpenses',
    code?: string
): any => {
    switch (strategyType) {
        case 'common':
            return {commonRow: []} as CommonWorkingPaper;
        case 'commonMaterialTest':
            return {commonRow: [], relatedRows: [], testRow: []} as CommonMaterialTestWorkingPaper;
        case 'commonTransaction':
            return {commonRow: [], relatedRows: []} as CommonTransactionWorkingPaper;
        case 'bank':
            return {bankRow: [], commonRow: []} as BankWorkingPaper;
        case 'feesTransaction':
            return {feesRow: [], relatedRows: []} as FeesTransactionWorkingPaper;
        case 'debtTransaction':
            return {debtRow: [], relatedRows: []} as DebtTransactionWorkingPaper;
        case 'debtFeesTransaction':
            return {debtFeeRow: [], relatedRows: []} as DebtFeesTransactionWorkingPaper;
        case 'inventories':
            return {commonRow: [], testRow: []} as InventoriesWorkingPaper;
        case 'commonInvestment':
            return {intangibleRows: [], principalSubsidiaryRow: []} as CommonInvestmentWorkingPaper;
        case 'dueFromToDirectors':
            return {dueFromToDirRow: []} as DueFromToDirectorsWorkingPaper;
        case 'gainLossExchange':
            return {gainExchange: [], lossExchange: []} as GainLossExchangeWorkingPaper;
        case 'gainLossDisposal':
            return {
                // PPE disposal rows
                locationOfPropertyDisRow: [],
                commercialAllowanceDisRow: [],
                industrialAllowanceDisRow: [],
                furnitureFixtureDisRow: [],
                computerHardSoftDisRow: [],
                fullPayMotorDisRow: [],
                hirePurchaseMotorDisRow: [],
                machineryDisRow: [],
                liftEscalatorDisRow: [],
                equipmentDisRow: [],
                // Other disposal rows
                subsidiaryRow: [],
                associateRow: [],
                intangibleRow: [],
                taxiLicenseDisRow: [],
                fullPayTaxiCabDisRow: [],
                hirePurchaseTaxiCabDisRow: [],
                trustsRow: [],
            } as GainLossDisposalWorkingPaper;
        case 'commissionPayment':
            return {commissionPayments: [], relatedItems: []} as CommissionPaymentWorkingPaper;
        case 'withholdingTax':
            return {whtRows: [], relatedItems: []} as WithholdingTaxWorkingPaper;
        case 'donation':
            return {taxDeductible: [], nonTaxDeductible: [], relatedItems: []} as DonationWorkingPaper;
        case 'purchases':
            return {
                purchases: [],
                relatedItems: [],
                transactionTest: [],
                cutOffThisYearTest: [],
                cutOffNextYearTest: []
            } as PurchasesWorkingPaper;
        case 'salesIncome':
            return {
                salesIncome: [],
                relatedRows: [],
                transactionTest: [],
                cutOffThisYearTest: [],
                cutOffNextYearTest: []
            } as SalesIncomeWorkingPaper;
        case 'propertyPlantEquipment':
            return {
                testRow: [],
                locationOfPropertyRow: [],
                commercialAllowanceRow: [],
                industrialAllowanceRow: [],
                furnitureFixtureRow: [],
                computerHardSoftRow: [],
                fullPayMotorRow: [],
                hirePurchaseMotorRow: [],
                machineryRow: [],
                liftEscalatorRow: [],
                equipmentRow: [],
                ppeRate: {
                    locationOfPropertyRate: 4,
                    commercialAllowanceRate: 20,
                    industrialAllowanceRate: 20,
                    furnitureFixtureRate: 20,
                    computerHardSoftRate: 20,
                    fullPayMotorRate: 30,
                    hirePurchaseMotorRate: 30,
                    machineryRate: 20,
                    liftEscalatorRate: 20,
                    equipmentRate: 20,
                    equipmentIrdRate: 20,
                },
            } as PropertyPlantEquipmentWorkingPaper;
        case 'intangibleAssets':
            return {
                intangibleRows: [],
                adjustments: {amorChgAdjusts: [], nbvCfAdjusts: []},
            } as IntangibleAssetsWorkingPaper;
        case 'investmentInTaxiCabsAndLicenses':
            return {
                taxiLicenseRow: [],
                fullPayTaxiCabRow: [],
                hirePurchaseTaxiCabRow: [],
            } as InvestmentInTaxiCabsAndLicensesWorkingPaper;
        case 'interestIncome':
            return {
                bankInterestIncome: [],
                otherInterestIncome: [],
                relatedRows: []
            } as InterestIncomeWorkingPaper;
        case 'salariesAllowance':
            return {
                staffRow: [],
                workerRow: [],
                directorRow: [],
                directorQuarterRow: [],
            } as SalariesAllowanceWorkingPaper;
        case 'salariesWage':
            return { salary: [] } as SalariesWageWorkingPaper;
        case 'directorSalary':
            return { directorSalary: [], quarter: [] } as DirectorSalaryWorkingPaper;
        case 'mpfSummary':
            return {
                mpfSummary: {
                    salary: { mpfTotal: 0, adjustmentTotal: 0, finalTotal: 0, lastYearTotal: 0 },
                    wages: { mpfTotal: 0, adjustmentTotal: 0, finalTotal: 0, lastYearTotal: 0 },
                    director: { mpfTotal: 0, adjustmentTotal: 0, finalTotal: 0, lastYearTotal: 0 },
                    total: { mpfTotal: 0, adjustmentTotal: 0, finalTotal: 0, lastYearTotal: 0 },
                },
            } as MpfSummaryWorkingPaper;
        case 'depreciationSummary':
            return {
                depreciationSummary: {
                    rows: [],
                    total: {
                        item: 'Total',
                        perClient: 0,
                        adjustment: 0,
                        finalTotal: 0,
                        lastYearFinal: 0,
                    },
                },
            } as DepreciationSummaryWorkingPaper;
        case 'interestSummary':
            return {
                interestSummary: {
                    bank: { interestTotal: 0, adjustmentTotal: 0, finalTotal: 0, lastYearTotal: 0 },
                    vehicle: { interestTotal: 0, adjustmentTotal: 0, finalTotal: 0, lastYearTotal: 0 },
                    total: { interestTotal: 0, adjustmentTotal: 0, finalTotal: 0, lastYearTotal: 0 },
                },
                addBackEnabled: true,
                addBackSourceCodes: [],
                addBackAmount: null,
                addBackPercent: null,
            } as InterestSummaryWorkingPaper;
        case 'loan':
            // For bank loans, return BankLoanWorkingPaper
            // Support both old codes (BANK_LOAN, NON_BANK_LOAN) and new codes (BS-CL-BLS, BS-NCL-BLS)
            if (code === 'BANK_LOAN' || code === 'NON_BANK_LOAN' ||
                code === 'BS-CL-BLS' || code === 'BS-NCL-BLS') {
                return {
                    bankLoans: []
                } as BankLoanWorkingPaper;
            }
            // For vehicle loans (BS-CL-OFL, BS-NCL-OFL), return VehicleLoanWorkingPaper
            return {
                vehicleLoans: []
            } as VehicleLoanWorkingPaper;
        case 'rentalExpenses':
            return {
                rows: [],
                rentalOtherInfo: {
                    monthsForPeriod: null,
                    extractPL: '0',
                    deletedMonthsByCol: {},
                    rentalTotalsByCol: {},
                },
            } as RentalExpensesWorkingPaper;
        default:
            return [];
    }
};

/**
 * 根据 scheduleCode 和 FsCategoryLine 创建初始的工作底稿数据
 * 用于在没有现有数据时初始化 Adj To Tab
 *
 * @param categoryLine - FsCategoryLine 对象，包含金额等信息
 * @param scheduleCode - 表单代码，用于判断数据类型
 * @param strategyType - 策略类型，决定返回的数据结构
 * @param options - 可选配置
 *   - wrapInProperty: 是否包装在属性对象中（如 { commonRow: [...] }）
 *   - propertyName: 包装时使用的属性名称
 * @returns 根据策略类型返回对应的初始数据结构
 *
 * 支持的策略类型：
 * - common: CommonWorkingPaper
 * - commonMaterialTest: CommonMaterialTestWorkingPaper
 * - commonTransaction: CommonTransactionWorkingPaper
 * - bank: BankWorkingPaper
 * - feesTransaction: FeesTransactionWorkingPaper
 * - debtTransaction: DebtTransactionWorkingPaper
 * - inventories: InventoriesWorkingPaper
 * - commonInvestment: commonInvestmentWorkingPaper
 * - dueFromToDirectors: DueFromToDirectorsWorkingPaper
 * - gainLossExchange: GainLossExchangeWorkingPaper
 * - gainLossDisposal: GainLossDisposalWorkingPaper
 */
export const createInitialRows = (
    categoryLine: FsCategoryLine | null,
    code: string,
    strategyType?: 'common' | 'commonMaterialTest' | 'commonTransaction' | 'bank' | 'feesTransaction' | 'debtTransaction' | 'inventories' | 'commonInvestment' | 'dueFromToDirectors' | 'gainLossExchange' | 'gainLossDisposal',
    options?: {
        wrapInProperty?: boolean;
        propertyName?: string;
    }
): any => {
    if (!categoryLine) {
        return strategyType ? getDefaultPayload(strategyType) : [];
    }

    // 如果指定了策略类型，按策略创建数据
    if (strategyType) {
        return createByStrategy(categoryLine, code, strategyType);
    }

    // 兼容旧逻辑：创建基础的 CommonRow 数组
    const initialRow: CommonRow = createInitialCommonRow(categoryLine, code, 1);
    const rows = [initialRow];

    // 如果需要包装在属性对象中
    if (options?.wrapInProperty) {
        const propertyName = options.propertyName || 'commonRow';
        return [{[propertyName]: rows}];
    }

    // 默认返回直接的数组
    return rows;
};

/**
 * 创建基础的 CommonRow 对象
 * @param categoryLine - FsCategoryLine 对象
 * @param scheduleCode - 表单代码
 * @param id - 行 ID
 * @returns CommonRow 对象
 */
const createInitialCommonRow = (
    categoryLine: FsCategoryLine,
    code: string,
    id: number
): CommonRow => {
    return {
        id,
        item: categoryLine.aliasEn || categoryLine.nameEn || code,
        itemEdit: true,
        perClient: categoryLine.amount || 0,
        finalAmount: categoryLine.finalAmount || 0,
        lastYearFinal: 0,
    };
};

/**
 * 创建基础的 BankRow 对象
 * @param categoryLine - FsCategoryLine 对象
 * @param bankId - 银行 ID
 * @returns BankRow 对象
 */
const createInitialBankRow = (
    categoryLine: FsCategoryLine,
    bankId: number
): BankRow => {
    return {
        bankId,
        bankName: '',
        accountNumber: '',
        perClient: categoryLine.amount || 0,
        lastYearFinal: 0,
    };
};

/**
 * 创建基础的 CommonFeesRow 对象
 * @param categoryLine - FsCategoryLine 对象
 * @param scheduleCode - 表单代码
 * @param id - 行 ID
 * @returns CommonFeesRow 对象
 */
const createInitialFeesRow = (
    categoryLine: FsCategoryLine,
    code: string,
    id: number
): CommonFeesRow => {
    return {
        id,
        date: null,
        invoiceNo: '',
        supplierName: categoryLine.aliasEn || categoryLine.nameEn || code,
        invDescription: null,
        perClient: categoryLine.amount || 0,
        lastYearFinal: 0,
    };
};

/**
 * 创建基础的 CommonDebtRow 对象
 * @param categoryLine - FsCategoryLine 对象
 * @param scheduleCode - 表单代码
 * @param id - 行 ID
 * @returns CommonDebtRow 对象
 */
const createInitialDebtRow = (
    categoryLine: FsCategoryLine,
    code: string,
    id: number
): CommonDebtRow => {
    return {
        id,
        invoiceDate: null,
        invoiceNo: '',
        customer: categoryLine.aliasEn || categoryLine.nameEn || code,
        perClient: categoryLine.amount || 0,
        lastYearFinal: 0,
    };
};

/**
 * 创建基础的 TestRow 对象
 * @param id - 行 ID
 * @returns TestRow 对象
 */
const createInitialTestRow = (id: number): TestRow => {
    return {
        id,
        date: null,
        invNo: '',
        description: '',
        amount: 0,
    };
};

/**
 * 根据策略类型创建初始工作底稿数据
 * @param categoryLine - FsCategoryLine 对象
 * @param scheduleCode - 表单代码
 * @param strategyType - 策略类型
 * @returns 对应策略的工作底稿对象
 */
const createByStrategy = (
    categoryLine: FsCategoryLine,
    code: string,
    strategyType: 'common' | 'commonMaterialTest' | 'commonTransaction' | 'bank' | 'feesTransaction' | 'debtTransaction' | 'inventories' | 'commonInvestment' | 'dueFromToDirectors' | 'gainLossExchange' | 'gainLossDisposal'
): any => {
    const initialCommonRow = createInitialCommonRow(categoryLine, code, 1);

    switch (strategyType) {
        case 'common':
            return {
                commonRow: [initialCommonRow]
            } as CommonWorkingPaper;

        case 'commonMaterialTest':
            return {
                commonRow: [initialCommonRow],
                relatedRows: [],
                testRow: [createInitialTestRow(1)]
            } as CommonMaterialTestWorkingPaper;

        case 'commonTransaction':
            return {
                commonRow: [initialCommonRow],
                relatedRows: []
            } as CommonTransactionWorkingPaper;

        case 'bank':
            return {
                bankRow: [createInitialBankRow(categoryLine, 1)],
                commonRow: [initialCommonRow]
            } as BankWorkingPaper;

        case 'feesTransaction':
            return {
                feesRow: [createInitialFeesRow(categoryLine, code, 1)],
                relatedRows: []
            } as FeesTransactionWorkingPaper;

        case 'debtTransaction':
            return {
                debtRow: [createInitialDebtRow(categoryLine, code, 1)],
                relatedRows: []
            } as DebtTransactionWorkingPaper;

        case 'inventories':
            return {
                commonRow: [initialCommonRow],
                testRow: [createInitialTestRow(1)]
            } as InventoriesWorkingPaper;

        case 'commonInvestment':
            return {
                intangibleRows: [{
                    id: (initialCommonRow as any).id ?? -1,
                    date: null,
                    item: String((initialCommonRow as any).item || 'Unlisted shares'),
                    costBf: (initialCommonRow as any).perClient ?? null,
                    addition: null,
                    costDisposal: null,
                    accAmorBf: null,
                    amorChg: null,
                    otherValue: null,
                    otherItem: null,
                    accDisposal: null,
                }],
                principalSubsidiaryRow: []
            } as CommonInvestmentWorkingPaper;

        case 'dueFromToDirectors':
            return {
                dueFromToDirRow: [{
                    id: (initialCommonRow as any).id,
                    annualId: undefined,
                    code: code,
                    directorId: undefined,
                    directorName: (initialCommonRow as any).item,
                    perClient: (initialCommonRow as any).perClient,
                    adjustments: (initialCommonRow as any).adjustments,
                    lastYearFinal: (initialCommonRow as any).lastYearFinal,
                }]
            } as DueFromToDirectorsWorkingPaper;

        case 'gainLossExchange':
            return {
                gainExchange: [initialCommonRow],
                lossExchange: []
            } as GainLossExchangeWorkingPaper;

        case 'gainLossDisposal':
            return {
                // PPE disposal rows
                locationOfPropertyDisRow: [],
                commercialAllowanceDisRow: [],
                industrialAllowanceDisRow: [],
                furnitureFixtureDisRow: [],
                computerHardSoftDisRow: [],
                fullPayMotorDisRow: [],
                hirePurchaseMotorDisRow: [],
                machineryDisRow: [],
                liftEscalatorDisRow: [],
                equipmentDisRow: [],
                // Other disposal rows
                subsidiaryRow: [],
                associateRow: [],
                intangibleRow: [],
                taxiLicenseDisRow: [],
                fullPayTaxiCabDisRow: [],
                hirePurchaseTaxiCabDisRow: [],
                trustsRow: [],
            } as GainLossDisposalWorkingPaper;

        default:
            return getDefaultPayload(strategyType, code);
    }
};

/**
 * Breakdown 模式下保存草稿的通用函数
 * 该函数会：
 * 1. 更新 adjustmentStore 中的 headerLine
 * 2. 调用后端 saveOrUpdate 接口进行保存
 * 3. 更新 store 中的 header 为后端返回的最新数据
 * 4. 调用成功回调函数（关闭对话框和刷新数据）
 *
 * @param adjustmentLine - 组件构建的 adjustmentLine 数据
 * @param showSnackbar - Snackbar 消息回调函数
 * @param onSuccess - 可选的成功回调函数，用于关闭对话框和刷新数据
 * @returns Promise<boolean> - 保存成功返回 true，失败返回 false
 */
/**
 * 非 Breakdown 模式下更新 store 的函数
 * 该函数会在本地更新 store 中的 headerLine，不会立即调用后端
 * 实际保存会在后续统一调用 saveOrUpdateByHeaderId
 *
 * @param adjustmentLine - 组件构建的 adjustmentLine 数据
 * @param showSnackbar - Snackbar 消息回调函数
 * @returns boolean - 更新成功返回 true，失败返回 false
 */
export const updateLocalDraft = (
    adjustmentLine: AuditAdjustmentLine,
    showSnackbar: ShowSnackbarFn
): boolean => {
    try {
        const store = useAdjustmentStore.getState();

        // 更新 store 中的 headerLine（本地更新，不调用后端）
        store.upsertHeaderLine(adjustmentLine);

        showSnackbar('Successfully saved adjustment line', 'success');
        return true;
    } catch (error: any) {
        const errorMessage = error?.message || 'Failed to save adjustment line';
        showSnackbar(errorMessage, 'error');
        console.error('updateLocalDraft error:', error);
        return false;
    }
};

export const saveBreakdownDraft = async (
    adjustmentLine: AuditAdjustmentLine,
    showSnackbar: ShowSnackbarFn,
    onSuccess?: () => void
): Promise<boolean> => {
    try {
        const store = useAdjustmentStore.getState();
        const currentMode = store.currentMode;
        const currentHeader = store.header;

        // 验证 header 是否存在
        if (!currentHeader) {
            showSnackbar('没有待保存的数据', 'warning');
            return false;
        }

        // 步骤 1: 准备要保存的数据（在清空前构建完整的 header）
        // 先在内存中构建包含新 line 的 header，但不更新 store
        const tempHeader = {...currentHeader};
        const lines: any[] = Array.isArray((tempHeader as any).lines) ? [...(tempHeader as any).lines] : [];

        // 找到或添加对应的 line
        const lineCode = String((adjustmentLine as any)?.code || (adjustmentLine as any)?.scheduleCode || '').trim();
        const lineKey = `${adjustmentLine.statement}|${lineCode}|${adjustmentLine.fsId}`;
        const existingIndex = lines.findIndex((line: any) =>
            `${line.statement}|${String((line as any)?.code || (line as any)?.scheduleCode || '').trim()}|${line.fsId}` === lineKey
        );

        // 前后端统一格式：details: [{ equipmentRow: [...], ppeRate: {...} }]
        // buildAdjustmentLine 已经构建了正确的格式，直接使用即可
        const formattedAdjustmentLine = {...adjustmentLine};

        if (existingIndex >= 0) {
            lines[existingIndex] = formattedAdjustmentLine;
        } else {
            lines.push(formattedAdjustmentLine);
        }

        (tempHeader as any).lines = lines;

        // 步骤 2: 调用后端接口保存
        await saveOrUpdate(tempHeader as any)

        // ✅ 設置數據已保存標誌，通知 FsAdjustment 需要重新加載數據
        useAdjustDraftStore.getState().setDataSaved(true);

        showSnackbar('保存成功', 'success');

        // 步骤 3: 调用成功回调（关闭对话框）
        // ❌ 不在这里清空 store - 让 FsAdjustment 在检测到关闭后再清除
        if (onSuccess) {
            onSuccess();
        }

        return true;
    } catch (error: any) {
        // 如果保存失败，恢复原始 header 数据
        const store = useAdjustmentStore.getState();
        if (store.header) {
            store.setHeader(store.header);
        }

        const errorMessage = error?.message || '保存失败';
        showSnackbar(errorMessage, 'error');
        console.error('saveBreakdownDraft error:', error);
        return false;
    }
};

/**
 * 提交单个 adjustment 的通用函数
 * 该函数会：
 * 1. 根据传入的 adjustmentLine 和 existingHeader 构建 header
 * 2. 如果存在 headerId，沿用原有的 header id；adjustmentLine 中已包含 lineId
 * 3. 设置 adjustmentMode = ADJUST_SINGLE, refNo = ''
 * 4. 调用后端 saveAndPosted 接口进行保存和提交
 * 5. 成功后设置数据已保存标志
 * 6. 调用成功回调函数（关闭对话框和刷新数据）
 *
 * @param adjustmentLine - 组件构建的 adjustmentLine 数据（已包含 lineId，如果存在）
 * @param showSnackbar - Snackbar 消息回调函数
 * @param headerId - 可选，从后端查询到的现有 header id
 * @param onSuccess - 可选的成功回调函数，用于关闭对话框和刷新数据
 * @returns Promise<boolean> - 保存成功返回 true，失败返回 false
 */
export const submitSingle = async (
    adjustmentLine: AuditAdjustmentLine,
    showSnackbar: ShowSnackbarFn,
    headerId?: string,
    onSuccess?: () => void
): Promise<boolean> => {
    try {
        const store = useAdjustmentStore.getState();

        // 构建 header 数据
        let headerToSubmit: AuditAdjustmentHeader;

        if (headerId) {
            // ✅ 如果存在 headerId，沿用原有的 header id
            // adjustmentLine 中已经包含了 lineId（如果存在）
            headerToSubmit = {
                id: headerId,  // 保留 headerId
                companyId: adjustmentLine.companyId,
                annualId: adjustmentLine.annualId,
                adjustmentMode: AdjustmentMode.ADJUST_SINGLE,
                refNo: '',
                status: 0,
                preparedUserId: 0,
                preparedDate: new Date().toISOString(),
                reviewedUserId: 0,
                reviewedDate: new Date().toISOString(),
                lines: [adjustmentLine]  // adjustmentLine 中已包含 lineId
            };
        } else {
            // ✅ 如果没有 headerId，创建新的 header
            headerToSubmit = {
                companyId: adjustmentLine.companyId,
                annualId: adjustmentLine.annualId,
                adjustmentMode: AdjustmentMode.ADJUST_SINGLE,
                refNo: '',
                status: 0,
                preparedUserId: 0,
                preparedDate: new Date().toISOString(),
                reviewedUserId: 0,
                reviewedDate: new Date().toISOString(),
                lines: [adjustmentLine]
            };
        }

        // 调用后端 saveAndPosted 接口
        await saveAndPosted(headerToSubmit);

        // ✅ 設置數據已保存標誌，通知 FsAdjustment 需要重新加載數據
        useAdjustDraftStore.getState().setDataSaved(true);

        showSnackbar('提交成功', 'success');

        // 调用成功回调（关闭对话框）
        // ❌ 不在这里清空 store - 让 FsAdjustment 在检测到关闭后再清除
        if (onSuccess) {
            onSuccess();
        }

        return true;
    } catch (error: any) {
        const errorMessage = error?.message || '提交失败';
        showSnackbar(errorMessage, 'error');
        console.error('submitSingle error:', error);
        return false;
    }
};

/**
 * 从 code 推断 strategyType
 * @param code - Schedule Code
 * @returns strategyType 字符串
 */
export const getStrategyTypeFromCode = (code: string): 'common' | 'commonMaterialTest' | 'commonTransaction' | 'bank' | 'feesTransaction' | 'debtTransaction' | 'inventories' | 'commonInvestment' | 'dueFromToDirectors' | 'gainLossExchange' | 'gainLossDisposal' | 'commissionPayment' | 'withholdingTax' | 'donation' | 'purchases' | 'salesIncome' | 'propertyPlantEquipment' | 'intangibleAssets' | 'investmentInTaxiCabsAndLicenses' | 'interestIncome' | 'salariesAllowance' | 'salariesWage' | 'directorSalary' | 'mpfSummary' | 'depreciationSummary' | 'interestSummary' | 'loan' | 'rentalExpenses' | null => {
    const upper = String(code || '').trim().toUpperCase();
    // Sales Income / Service Income（SalesIncomeWp，salesIncome[] 结构）
    if (upper === 'PL-TURN-SI' || upper === 'PL-TURN-SE') return 'salesIncome';
    if (upper === 'PL-TURN-RI') return 'rentalExpenses';
    if (upper === 'PL-AE-MPF') return 'mpfSummary';
    if (upper === 'PL-AE-DEP') return 'depreciationSummary';
    if (upper === 'PL-AE-WHT') return 'withholdingTax';
    if (upper === 'PL-OI-II' || upper === 'PL-OI-SFG') return 'interestIncome';
    if (upper === 'PL-FC-LI') return 'interestSummary';
    // 基于 scheduleCode 的模式推断策略类型
    if (upper.includes('DON')) return 'donation';
    if (upper.includes('PUR')) return 'purchases';
    if (upper.includes('SAL') && upper.includes('INC')) return 'salesIncome';
    if (upper.includes('COM') && upper.includes('PAY')) return 'commissionPayment';
    if (upper.includes('INT') && upper.includes('INC')) return 'interestIncome';
    if (upper.includes('GAIN') || upper.includes('LOSS')) {
        if (upper.includes('EXC')) return 'gainLossExchange';
        if (upper.includes('DIS')) return 'gainLossDisposal';
    }
    if (upper === 'BS-NCA-ISUB' || upper === 'BS-NCA-IASS' || upper === 'BS-CA-IOT') return 'commonInvestment';
    if (upper.includes('INV')) return 'inventories';
    if (upper.includes('PPE')) return 'propertyPlantEquipment';
    // BS-NCA-IA / PL-AE-AIA (not "ITA"); keep ITA for legacy schedule keys
    if (upper === 'BS-NCA-IA' || upper === 'PL-AE-AIA' || upper.includes('ITA')) return 'intangibleAssets';
    // ITL-only: code has no "TAXI" substring; keep IA/AIA branch above unchanged
    if (upper === 'BS-NCA-ITL') return 'investmentInTaxiCabsAndLicenses';
    if (upper.includes('TAXI')) return 'investmentInTaxiCabsAndLicenses';
    if (upper === 'BS-CA-CASH' || upper === 'BS-CL-BOD') return 'bank';
    if (upper.includes('BANK')) return 'bank';
    if (upper.includes('LOAN')) return 'loan';
    if (upper.includes('DUE') || upper === 'BS-CA-DIR' || upper === 'BS-CL-DTD') return 'dueFromToDirectors';
    if (upper.includes('REN')) return 'rentalExpenses';
    if (upper.includes('SAL') && upper.includes('ALL')) return 'salariesAllowance';

    // 默认返回 common
    return 'common';
};

/**
 * 计算组件的总调整值（支持多组数据的组件如 Donation, Purchases 等）
 * @param detail - 组件详细数据
 * @param code - Code
 * @returns 总调整值
 */
export const calculateTotalAdjustment = (detail: any, code: string): number => {
    if (!detail) return 0;

    // BS-EQ-DIV / BS-EQ-AL flat：top-level adjustment；带 commonRow 时走下方常规汇总
    if ((code === 'BS-EQ-DIV' || code === 'BS-EQ-AL')
        && typeof detail === 'object'
        && !(Array.isArray(detail?.commonRow) && detail.commonRow.length > 0)) {
        return resolveFlatEquityAdjustment(detail);
    }

    // PL-AE-DEP Taxi nest / slim only — do not change IA / AIA / ITL / PPE totals
    const upperCode = String(code || '').trim().toUpperCase();
    if (upperCode === 'PL-AE-DEP' && Array.isArray(detail?.intangibleRows)) {
        return detail.intangibleRows.reduce((sum: number, row: any) => {
            if (!row || typeof row !== 'object') return sum;
            const adj = row.adjustments;
            if (adj && typeof adj === 'object' && Array.isArray(adj.amorChgAdjs)) {
                const fromList = adj.amorChgAdjs.reduce(
                    (s: number, e: any) => s + (Number(e?.adjustment) || 0),
                    0,
                );
                if (Math.abs(fromList) >= 0.001) return sum + fromList;
            }
            const nested = Number(row.amorChgAdj) || 0;
            const classic = Number(row.adjustment) || 0;
            return sum + (Math.abs(nested) >= 0.001 ? nested : classic);
        }, 0);
    }
    if (
        upperCode === 'PL-AE-DEP'
        && (detail.amorChgAdj != null || detail.adjustment != null)
        && !Array.isArray(detail?.locationOfPropertyRow)
        && !Array.isArray(detail?.machineryRow)
        && !Array.isArray(detail?.equipmentRow)
    ) {
        const owned = Number(detail.amorChgAdj) || 0;
        if (Math.abs(owned) >= 0.001) return owned;
        // Slim Taxi field adj may only carry top-level adjustment
        if (detail.intangibleRowId != null || detail.amorChgAdj != null) {
            return Number(detail.adjustment) || 0;
        }
    }
    if (upperCode === 'PL-AE-DEP' && detail?.depreciationSummary) {
        return Number(detail.depreciationSummary?.total?.adjustment) || 0;
    }

    // 从 code 推断 strategyType
    const strategyType = getStrategyTypeFromCode(code);
    if (!strategyType) {
        // 如果无法推断，尝试提取通用行数据
        const rows = extractRowsDetails(detail);
        if (rows && Array.isArray(rows)) {
            return rows.reduce((sum: number, row: any) => sum + (Number(row.adjustment) || 0), 0);
        }
        return 0;
    }

    // 使用 extractByStrategy 提取标准化数据
    const extracted = extractByStrategy(detail, strategyType, code);
    if (!extracted) return 0;

    let totalAdjustment = 0;

    // 根据不同的 strategyType 处理不同的数据结构
    switch (strategyType) {
        case 'donation': {
            // Donation 有两组数据：taxDeductible 和 nonTaxDeductible
            const donationData = extracted as DonationWorkingPaper;
            if (donationData.taxDeductible && Array.isArray(donationData.taxDeductible)) {
                totalAdjustment += donationData.taxDeductible.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (donationData.nonTaxDeductible && Array.isArray(donationData.nonTaxDeductible)) {
                totalAdjustment += donationData.nonTaxDeductible.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }

        case 'purchases': {
            // Purchases 有多组数据
            const purchasesData = extracted as PurchasesWorkingPaper;
            if (purchasesData.purchases && Array.isArray(purchasesData.purchases)) {
                totalAdjustment += purchasesData.purchases.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (purchasesData.transactionTest && Array.isArray(purchasesData.transactionTest)) {
                totalAdjustment += purchasesData.transactionTest.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (purchasesData.cutOffThisYearTest && Array.isArray(purchasesData.cutOffThisYearTest)) {
                totalAdjustment += purchasesData.cutOffThisYearTest.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (purchasesData.cutOffNextYearTest && Array.isArray(purchasesData.cutOffNextYearTest)) {
                totalAdjustment += purchasesData.cutOffNextYearTest.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }

        case 'salesIncome': {
            // SalesIncome 有多组数据
            const salesData = extracted as SalesIncomeWorkingPaper;
            if (salesData.salesIncome && Array.isArray(salesData.salesIncome)) {
                totalAdjustment += salesData.salesIncome.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (salesData.transactionTest && Array.isArray(salesData.transactionTest)) {
                totalAdjustment += salesData.transactionTest.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (salesData.cutOffThisYearTest && Array.isArray(salesData.cutOffThisYearTest)) {
                totalAdjustment += salesData.cutOffThisYearTest.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (salesData.cutOffNextYearTest && Array.isArray(salesData.cutOffNextYearTest)) {
                totalAdjustment += salesData.cutOffNextYearTest.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }

        case 'commissionPayment': {
            // CommissionPayment 有 commissionPayments 数组
            const commissionData = extracted as CommissionPaymentWorkingPaper;
            if (commissionData.commissionPayments && Array.isArray(commissionData.commissionPayments)) {
                totalAdjustment += commissionData.commissionPayments.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }

        case 'withholdingTax': {
            const whtData = extracted as WithholdingTaxWorkingPaper;
            if (whtData.whtRows && Array.isArray(whtData.whtRows)) {
                totalAdjustment += whtData.whtRows.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }

        case 'commonMaterialTest': {
            // CommonMaterialTest 有 commonRow 和 testRow
            const materialTestData = extracted as CommonMaterialTestWorkingPaper;
            if (materialTestData.commonRow && Array.isArray(materialTestData.commonRow)) {
                totalAdjustment += materialTestData.commonRow.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (materialTestData.testRow && Array.isArray(materialTestData.testRow)) {
                totalAdjustment += materialTestData.testRow.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }

        case 'inventories': {
            // Inventories 有 commonRow 和 testRow
            const inventoriesData = extracted as InventoriesWorkingPaper;
            if (inventoriesData.commonRow && Array.isArray(inventoriesData.commonRow)) {
                totalAdjustment += inventoriesData.commonRow.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (inventoriesData.testRow && Array.isArray(inventoriesData.testRow)) {
                totalAdjustment += inventoriesData.testRow.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }

        case 'gainLossExchange': {
            // GainLossExchange 有 gainExchange 和 lossExchange
            const gainLossData = extracted as GainLossExchangeWorkingPaper;
            if (gainLossData.gainExchange && Array.isArray(gainLossData.gainExchange)) {
                totalAdjustment += gainLossData.gainExchange.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (gainLossData.lossExchange && Array.isArray(gainLossData.lossExchange)) {
                totalAdjustment += gainLossData.lossExchange.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }

        case 'interestIncome': {
            // InterestIncome 有 bankInterestIncome 和 otherInterestIncome
            const interestData = extracted as InterestIncomeWorkingPaper;
            if (interestData.bankInterestIncome && Array.isArray(interestData.bankInterestIncome)) {
                totalAdjustment += interestData.bankInterestIncome.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            if (interestData.otherInterestIncome && Array.isArray(interestData.otherInterestIncome)) {
                totalAdjustment += interestData.otherInterestIncome.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }

        case 'dueFromToDirectors': {
            const dirData = extracted as DueFromToDirectorsWorkingPaper;
            if (dirData.dueFromToDirRow && Array.isArray(dirData.dueFromToDirRow)) {
                totalAdjustment += dirData.dueFromToDirRow.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }

        default: {
            // 对于其他类型（common, bank 等），尝试提取通用行数据
            const rows = extractRowsDetails(extracted);
            if (rows && Array.isArray(rows)) {
                totalAdjustment = rows.reduce((sum: number, row: any) =>
                    sum + (Number(row.adjustment) || 0), 0);
            }
            break;
        }
    }

    return totalAdjustment;
};

// ── 行校驗通用工具 ─────────────────────────────────────────────────────────

/**
 * 統一的行級校驗錯誤類型
 * key = row.id, value = { fieldName: errorMessage }
 * 例：{ 3: { item: 'Item name is required' }, 5: { supplierName: 'Supplier Name is required' } }
 */
export type WpRowErrors = Record<number, Record<string, string>>;

/** 行校驗配置 */
export interface RowValidationConfig<T> {
    /** 標識字段名（如 'item', 'supplierName', 'name'） */
    identityField: keyof T;
    /** perClient 字段名（如 'perClient', 'costBf', 'salary'） */
    amountField: keyof T;
    /** adjustment 字段名（如 'adjustment', 'additions'），可選 */
    adjustmentField?: keyof T;
    /**
     * 額外金額字段（如 'lastYearFinal'）：任一非空則不算全空行。
     * 避免只填上年 Final 時被 Step1 丢掉，导致静默自动保存写成空数组并软删。
     */
    extraAmountFields?: (keyof T)[];
    /** 校驗失敗時的錯誤消息 */
    errorMessage: string;
}

/** 通用行校驗返回值 */
export interface RowValidationResult<T> {
    valid: boolean;
    errors: WpRowErrors;
    filteredRows: T[];
}

function isAmountFieldEmpty(val: unknown): boolean {
    return (
        val == null ||
        val === 0 ||
        (typeof val === 'string' && val.trim() === '')
    );
}

/**
 * 通用行校驗函數工廠
 * Step 1: 全空行移除（標識 + 金額 + 調整 + 額外金額字段 全空）
 * Step 2: 非空校驗（標識字段不可為空）
 */
export function createRowValidator<T extends { id?: number | null }>(
    config: RowValidationConfig<T>
) {
    const {identityField, amountField, adjustmentField, extraAmountFields, errorMessage} = config;

    return (rows: T[]): RowValidationResult<T> => {
        const filteredRows: T[] = [];
        const errors: WpRowErrors = {};
        let hasErrors = false;

        rows.forEach(row => {
            const idVal = row[identityField];
            const idEmpty = !idVal || (typeof idVal === 'string' && idVal.trim() === '');
            const amtEmpty = isAmountFieldEmpty(row[amountField] as unknown);
            const adjVal = adjustmentField ? (row[adjustmentField] as unknown) : null;
            const adjEmpty = !adjustmentField || isAmountFieldEmpty(adjVal);
            const extraEmpty = !(extraAmountFields || []).some(
                (field) => !isAmountFieldEmpty(row[field] as unknown),
            );

            // Step 1: 全空行移除
            if (idEmpty && amtEmpty && adjEmpty && extraEmpty) return;

            // Step 2: 非空校驗
            if (idEmpty) {
                errors[(row as any).id ?? -1] = {[identityField as string]: errorMessage};
                hasErrors = true;
            }

            filteredRows.push(row);
        });

        return {valid: !hasErrors, errors, filteredRows};
    };
}
